import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { useLang } from "@/i18n";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const STORAGE_KEY = "atlas_concierge_messages";
const NUDGE_SEEN_KEY = "atlas_concierge_nudge_seen";
const NUDGE_DELAY_MS = 8000;

const QUICK_QUESTIONS = {
  de: ["Wann ist Check-in?", "Gibt es WLAN?", "Wie storniere ich?", "Haustiere erlaubt?"],
  uk: ["Коли заїзд?", "Чи є Wi-Fi?", "Як скасувати бронювання?", "Чи можна з тваринами?"],
  en: ["What's check-in time?", "Is there WiFi?", "How do I cancel?", "Are pets allowed?"],
  fr: ["Quelle heure est le check-in ?", "Y a-t-il du WiFi ?", "Comment annuler ?", "Animaux acceptés ?"],
  nl: ["Hoe laat is check-in?", "Is er wifi?", "Hoe annuleer ik?", "Zijn huisdieren toegestaan?"],
  pl: ["O której jest zameldowanie?", "Czy jest WiFi?", "Jak anulować?", "Czy zwierzęta są dozwolone?"],
  tr: ["Giriş saati kaçta?", "WiFi var mı?", "Nasıl iptal ederim?", "Evcil hayvana izin var mı?"],
  ru: ["Во сколько заезд?", "Есть Wi-Fi?", "Как отменить бронь?", "Можно с животными?"],
};

const GREETING = {
  de: "Hallo! Ich bin der Atlas-Concierge. Frag mich gerne zu Check-in, WLAN, Lage oder allem rund um deinen Aufenthalt.",
  uk: "Привіт! Я консьєрж Atlas. Питай про заїзд, Wi-Fi, розташування або будь-що про твоє проживання.",
  en: "Hi! I'm the Atlas concierge. Ask me anything about check-in, WiFi, location, or your stay.",
  fr: "Bonjour ! Je suis le concierge Atlas. Posez-moi vos questions sur l'arrivée, le WiFi, l'emplacement ou votre séjour.",
  nl: "Hallo! Ik ben de Atlas-concierge. Vraag me gerust over check-in, wifi, locatie of je verblijf.",
  pl: "Cześć! Jestem concierge Atlas. Pytaj śmiało o zameldowanie, WiFi, lokalizację lub cokolwiek związanego z pobytem.",
  tr: "Merhaba! Ben Atlas concierge'iyim. Check-in, WiFi, konum veya konaklamanla ilgili her şeyi sorabilirsin.",
  ru: "Привет! Я консьерж Atlas. Спрашивай про заезд, Wi-Fi, расположение или что угодно о проживании.",
};

const UNAVAILABLE_MSG = {
  de: "Entschuldige, gerade nicht erreichbar. Bitte versuch es später oder schreib uns direkt: info@atlas-apartment.de",
  uk: "Вибач, зараз недоступно. Спробуй пізніше або напиши нам напряму: info@atlas-apartment.de",
  en: "Sorry, I'm unavailable right now. Please try again later or email us directly: info@atlas-apartment.de",
  fr: "Désolé, indisponible pour le moment. Réessayez plus tard ou écrivez-nous directement : info@atlas-apartment.de",
  nl: "Sorry, momenteel niet beschikbaar. Probeer het later opnieuw of mail ons direct: info@atlas-apartment.de",
  pl: "Przepraszamy, chwilowo niedostępne. Spróbuj później lub napisz bezpośrednio: info@atlas-apartment.de",
  tr: "Üzgünüm, şu an ulaşılamıyor. Lütfen daha sonra tekrar dene veya doğrudan yaz: info@atlas-apartment.de",
  ru: "Извини, сейчас недоступно. Попробуй позже или напиши напрямую: info@atlas-apartment.de",
};

const ASK_PLACEHOLDER = {
  de: "Frage stellen…", uk: "Постав питання…", en: "Ask a question…",
  fr: "Posez une question…", nl: "Stel een vraag…", pl: "Zadaj pytanie…",
  tr: "Bir soru sor…", ru: "Задать вопрос…",
};

const STATUS_LABEL = {
  de: "Online · antwortet sofort", uk: "Онлайн · відповідає миттєво", en: "Online · replies instantly",
  fr: "En ligne · répond instantanément", nl: "Online · reageert direct", pl: "Online · odpowiada natychmiast",
  tr: "Çevrimiçi · anında yanıtlar", ru: "Онлайн · отвечает мгновенно",
};

const NUDGE_TEXT = {
  de: "👋 Fragen zu deinem Aufenthalt? Ich helfe gerne!",
  uk: "👋 Питання про проживання? Радо допоможу!",
  en: "👋 Questions about your stay? Happy to help!",
  fr: "👋 Des questions sur votre séjour ? Je suis là pour vous aider !",
  nl: "👋 Vragen over je verblijf? Ik help je graag!",
  pl: "👋 Masz pytania o pobyt? Chętnie pomogę!",
  tr: "👋 Konaklamanla ilgili sorun mu var? Yardımcı olmaktan mutluluk duyarım!",
  ru: "👋 Есть вопросы о проживании? С радостью помогу!",
};

/** Sehr leichtes Markdown: **fett** und Zeilenumbrüche - Gemini antwortet gelegentlich damit. */
function renderRich(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part.split("\n").map((line, j, arr) => (
      <React.Fragment key={`${i}-${j}`}>{line}{j < arr.length - 1 && <br />}</React.Fragment>
    ));
  });
}

export function ConciergeChat() {
  const { lang } = useLang();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [rendered, setRendered] = useState(false); // für Exit-Animation
  const [showNudge, setShowNudge] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false); // true = Tippt-Punkte, bis der erste echte Textteil ankommt
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/concierge/status`).then((r) => r.json()).then((d) => setEnabled(!!d.enabled)).catch(() => setEnabled(false));
  }, []);

  // Proaktive Sprechblase nach ein paar Sekunden, aber nur einmal pro Browser
  useEffect(() => {
    if (!enabled) return;
    try {
      if (sessionStorage.getItem(NUDGE_SEEN_KEY)) return;
    } catch {}
    const tm = setTimeout(() => setShowNudge(true), NUDGE_DELAY_MS);
    return () => clearTimeout(tm);
  }, [enabled]);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch {}
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Öffnen/Schließen mit sauberer Exit-Animation statt hartem Verschwinden
  useEffect(() => {
    if (open) {
      setRendered(true);
      dismissNudge();
    } else if (rendered) {
      const tm = setTimeout(() => setRendered(false), 220);
      return () => clearTimeout(tm);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  // Klick außerhalb schließt den Chat
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest("[data-concierge-toggle]")) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const dismissNudge = () => {
    setShowNudge(false);
    try { sessionStorage.setItem(NUDGE_SEEN_KEY, "1"); } catch {}
  };

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const tr8 = (map) => map[lang] || map.de;

  /** Echtes Live-Streaming: liest die SSE-Antwort vom Backend Stück für Stück und
   *  aktualisiert die Chat-Blase laufend - kein Warten auf die komplette Antwort. */
  const sendText = async (text) => {
    if (!text.trim() || sending) return;
    const userMsg = { role: "user", content: text.trim() };
    const historyForApi = [...messages, userMsg];
    const assistantIndex = historyForApi.length;
    setMessages([...historyForApi, { role: "assistant", content: "" }]);
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    setSending(true);

    let accumulated = "";
    let sawError = false;
    let sawAnyText = false;

    const setAssistantContent = (content) => {
      setMessages((m) => {
        const copy = [...m];
        copy[assistantIndex] = { role: "assistant", content };
        return copy;
      });
    };

    try {
      const resp = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: historyForApi, lang }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream request failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // letztes, evtl. unvollständiges Event für den nächsten Durchlauf aufheben

        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let json;
          try { json = JSON.parse(payload); } catch { continue; }
          if (json.error) { sawError = true; continue; }
          if (json.delta) {
            if (!sawAnyText) { setSending(false); sawAnyText = true; }
            accumulated += json.delta;
            setAssistantContent(accumulated);
          }
        }
      }

      if (!sawAnyText) {
        setAssistantContent(tr8(UNAVAILABLE_MSG));
      } else if (sawError && !accumulated) {
        setAssistantContent(tr8(UNAVAILABLE_MSG));
      }
    } catch {
      setAssistantContent(tr8(UNAVAILABLE_MSG));
    } finally {
      setSending(false);
    }
  };

  if (!enabled) return null;

  const quickQuestions = QUICK_QUESTIONS[lang] || QUICK_QUESTIONS.de;

  return (
    <>
      {/* Proaktive Sprechblase */}
      {showNudge && !open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-[92px] right-6 z-50 max-w-[240px] bg-white rounded-2xl rounded-br-sm shadow-[0_12px_40px_rgba(19,22,15,0.2)] border border-[var(--warm)] px-4 py-3 text-left text-sm text-[var(--ink)] hover:-translate-y-0.5 transition-transform fade-in"
        >
          <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[var(--terra)] text-white text-[10px] flex items-center justify-center">1</span>
          {tr8(NUDGE_TEXT)}
        </button>
      )}

      <button
        data-concierge-toggle
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-6 z-50 flex items-center justify-center w-14 h-14 bg-[var(--sage)] hover:bg-[var(--sage-dark)] text-white rounded-full shadow-lg transition-all hover:scale-110"
        title="Atlas Concierge"
        aria-label="Atlas Concierge"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && !showNudge && messages.length === 0 && (
          <span className="absolute top-0 right-0 w-3 h-3 rounded-full bg-[var(--terra)] ring-2 ring-white animate-pulse" />
        )}
      </button>

      {rendered && (
        <div
          ref={panelRef}
          className="fixed z-50 bg-white shadow-[0_24px_64px_rgba(19,22,15,0.25)] border border-[var(--warm)] flex flex-col overflow-hidden
                     inset-0 sm:inset-auto sm:bottom-[168px] sm:right-6 sm:w-[380px] sm:h-[600px] sm:max-h-[70vh] rounded-none sm:rounded-[24px]"
          style={{
            opacity: open ? 1 : 0,
            transform: open ? "translateY(0) scale(1)" : "translateY(12px) scale(0.97)",
            transformOrigin: "bottom right",
            transition: "opacity 0.22s cubic-bezier(0.16,1,0.3,1), transform 0.22s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          {/* Header */}
          <div className="bg-[var(--sage)] text-white px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="relative shrink-0">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                  <Sparkles size={16} className="text-[var(--gold)]" />
                </div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#4ADE80] ring-2 ring-[var(--sage)]" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">Atlas Concierge</p>
                <p className="text-[11px] text-white/60 leading-tight">{tr8(STATUS_LABEL)}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="sm:hidden p-1.5 -mr-1.5 text-white/80 hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--cream)]">
            <div className="flex justify-start">
              <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-bl-sm bg-white border border-[var(--warm)] text-sm text-[var(--ink)] leading-relaxed">
                {tr8(GREETING)}
              </div>
            </div>

            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {quickQuestions.map((q) => (
                  <button key={q} onClick={() => sendText(q)}
                    className="text-xs px-3 py-2 rounded-full border border-[var(--terra)]/40 text-[var(--terra-dark)] bg-white hover:bg-[var(--terra)] hover:text-white hover:border-[var(--terra)] transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => {
              const isStreamingEmpty = m.role === "assistant" && m.content === "" && sending && i === messages.length - 1;
              if (isStreamingEmpty) return null; // Tippt-Punkte-Bubble übernimmt das solange nichts da ist
              return (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[var(--terra)] text-white rounded-2xl rounded-br-sm"
                      : "bg-white border border-[var(--warm)] text-[var(--ink)] rounded-2xl rounded-bl-sm"}`}>
                    {renderRich(m.content)}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-white border border-[var(--warm)] flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="w-1.5 h-1.5 rounded-full bg-[var(--muted)] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[var(--warm)] flex items-end gap-2 bg-white shrink-0">
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(input); }
              }}
              placeholder={tr8(ASK_PLACEHOLDER)}
              className="flex-1 resize-none border border-[var(--warm)] rounded-2xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[var(--terra)] leading-snug max-h-[120px]"
            />
            <button onClick={() => sendText(input)} disabled={sending || !input.trim()}
              className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-[var(--terra)] hover:bg-[var(--terra-dark)] disabled:opacity-40 text-white transition-colors">
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
